import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  contentChildren,
  ElementRef,
  HostBinding,
  inject,
  Input,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
  Validators,
} from '@angular/forms';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
import { ButtonVariant, FmntsButtonModule } from '@fmnts/components/button';
import { htmlIdMaker } from '@fmnts/components/core';
import {
  AbstractFormFieldControl,
  FmntsFormFieldControl,
  FmntsFormFieldModule,
} from '@fmnts/components/form-field';
import { merge, startWith, switchMap, takeUntil } from 'rxjs';
import {
  ButtonToggleComponent,
  FMNTS_SEGMENTED_BUTTON_GROUP_COMPONENT,
} from './button-toggle.component';

const segmentedButtonId = htmlIdMaker('fmnts-segmnented-button');

/**
 * Segmented buttons help people select options, switch views, or sort elements
 *
 * @example
 *   <fmnts-segmented-button>
 *     <fmnts-button-toggle value="list">
 *       List View
 *     </fmnts-button-toggle>
 *     <fmnts-button-toggle value="grid">
 *       Grid View
 *     </fmnts-button-toggle>
 *   </fmnts-segmented-button>`,
 */
@Component({
  selector: 'fmnts-segmented-button',
  templateUrl: './segmented-button.component.html',
  styleUrls: ['./segmented-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FmntsButtonModule, FormsModule, FmntsFormFieldModule],
  standalone: true,
  providers: [
    {
      provide: FMNTS_SEGMENTED_BUTTON_GROUP_COMPONENT,
      useExisting: SegmentedButtonComponent,
    },
    { provide: FmntsFormFieldControl, useExisting: SegmentedButtonComponent },
  ],
})
export class SegmentedButtonComponent
  extends AbstractFormFieldControl<string>
  implements ControlValueAccessor, AfterContentInit
{
  public readonly cd = inject(ChangeDetectorRef);
  protected readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  override readonly ngControl = inject(NgControl, {
    optional: true,
    self: true,
  });

  protected readonly _uid = segmentedButtonId.next().value;

  override onContainerClick(event: MouseEvent): void {}
  override setDescribedByIds(ids: string[]): void {
    if (ids.length) {
      this._elementRef.nativeElement.setAttribute(
        'aria-describedby',
        ids.join(' '),
      );
    } else {
      this._elementRef.nativeElement.removeAttribute('aria-describedby');
    }
  }

  @HostBinding('class.fmnts-segmented-button') private readonly componentClass =
    'fmnts-segmented-button';

  public get value(): string | null {
    return this._value;
  }
  public set value(value: string) {
    this._value = value;
    this._onChange(value);
    this._onTouched();
  }
  private _value: string | null = null;

  @Input()
  get id(): string {
    return this._id();
  }
  set id(value: string) {
    this._id.set(value || this._uid);
  }
  protected readonly _id = signal(this._uid);

  override get empty(): boolean {
    return this.selectionModel.isEmpty();
  }

  /** Whether the control is required. */
  @Input({ transform: booleanAttribute })
  get required(): boolean {
    return (
      this._required() ??
      this.ngControl?.control?.hasValidator(Validators.required) ??
      false
    );
  }
  set required(value: boolean) {
    this._required.set(value);
    this.stateChanges.next();
  }
  private readonly _required = signal<boolean | undefined>(undefined);

  /**
   * `true`, if the control should be disabled.
   */
  @HostBinding('class.fmnts-segmented-button--disabled')
  @HostBinding('attr.aria-disabled')
  @Input({ transform: booleanAttribute })
  disabled = false;

  protected readonly buttons = contentChildren(ButtonToggleComponent, {
    descendants: true,
  });

  /** Deals with the selection logic. */
  protected selectionModel: SelectionModel<ButtonToggleComponent> =
    new SelectionModel();

  /**
   * TODO: add second variant from design
   */
  public variant = input<ButtonVariant>('stroked');

  private _onChange: ChangeCallback<string> = () => {};
  private _onTouched: TouchedCallback = () => {};

  constructor() {
    super();

    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    /** Combined stream of all of the child options' change events. */
    toObservable(this.buttons)
      .pipe(
        switchMap((buttons) =>
          merge(...buttons.map((button) => button.selectionChange)),
        ),
      )
      .subscribe((event) => this.onSelect(event.source, event.isUserInput));

    // Initialize and listen to changes in options
    toObservable(this.buttons)
      .pipe(startWith(null), takeUntil(this.destroyed$))
      .subscribe((c) => {
        this._initializeSelection();
      });
  }

  ngAfterContentInit(): void {
    // Listen to Model changes, and update component value accordingly
    this.selectionModel.changed
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        event.added.forEach((toggle) => toggle.select());
        event.removed.forEach((toggle) => toggle.deselect());
        this.cd.markForCheck();
      });
  }

  public writeValue(value: string): void {
    if (value !== this.value) {
      if (this.buttons()) {
        this._setSelectionByValue(value);
      }
      this._value = value;
    }
  }

  public registerOnChange(fn: ChangeCallback): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: TouchedCallback): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  private _setSelectionByValue(value: unknown): ButtonToggleComponent | null {
    const correspondingToggles = this.buttons().find(
      (option: ButtonToggleComponent) => {
        // Skip options that are already in the model
        if (this.selectionModel.isSelected(option)) {
          return false;
        }

        return option.value() === value;
      },
    );

    if (correspondingToggles) {
      correspondingToggles.select();
      this.selectionModel.select(correspondingToggles);
    }

    return correspondingToggles ? correspondingToggles : null;
  }

  private _initializeSelection(): void {
    // Defer setting the value in order to avoid the "Expression
    // has changed after it was checked" errors from Angular.
    void Promise.resolve().then(() => {
      if (this.ngControl) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.value = this.ngControl.value || null;
      }

      this._setSelectionByValue(this.value);
    });
  }

  protected onSelect(
    option: ButtonToggleComponent,
    isUserInput: boolean,
  ): void {
    const wasSelected = this.selectionModel.isSelected(option);

    // new value was set
    if (wasSelected !== option.checked()) {
      if (option.checked()) {
        this.selectionModel.select(option);
      } else {
        this.selectionModel.deselect(option);
      }

      this._onChange(option.value());
      this._onTouched();
    }
  }

  /** Checks whether a button toggle is selected. */
  isSelected(toggle: ButtonToggleComponent): boolean {
    return this.selectionModel && this.selectionModel.isSelected(toggle);
  }
}
